/**
 * ScanQBButton — "Scan QB" for the Invoices master database. Pre-scan dialog
 * is a date range ONLY (no search word): pulls every QuickBooks invoice in
 * the range and adds the new ones (duplicates by invoice # auto-skip).
 * The scan runs server-side: closing the window never cancels it, and if the
 * app reloads mid-scan the window resumes from the saved job.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ScanSearch, X } from "lucide-react";
import api from "@/lib/api";

const Overlay = ({ children, onClose }) => (
  <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
    <div className="max-h-[88vh] w-full max-w-md overflow-y-auto rounded-xl border border-white/15 bg-[#0d0d0d] p-5 shadow-2xl"
      onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

export default function ScanQBButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(today());
  const [running, setRunning] = useState(null);  // live job while scanning
  const [done, setDone] = useState(null);        // finished job summary
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const beginPolling = (jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/master/out-invoices/scan/${jobId}`);
        if (r.data.status === "done" || r.data.status === "error") {
          clearInterval(pollRef.current);
          setRunning(null);
          if (r.data.status === "error") {
            toast.error(`Scan failed: ${r.data.error || "unknown error"}`);
            setDone(null);
            return;
          }
          setDone(r.data);
          onDone?.();
        } else {
          setRunning(r.data);
        }
      } catch { /* keep polling */ }
    }, 2000);
  };

  // Resume an in-flight (or just-finished) scan after an app reload — the
  // job runs on the server and survives the app closing.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/master/out-invoices/scan/latest");
        const job = data?.job;
        if (!job) return;
        if (job.status === "done") setDone(job);
        else { setRunning(job); beginPolling(job.job_id); }
      } catch { /* none */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    if (!from || !to) { toast.error("Pick both dates"); return; }
    try {
      const { data } = await api.post("/master/out-invoices/scan/start", { date_from: from, date_to: to });
      setDone(null);
      setRunning({ job_id: data.job_id, status: "running", fetched: 0, added: 0, skipped: 0 });
      beginPolling(data.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the QuickBooks scan");
    }
  };

  const busy = !!running;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        data-testid="invoices-scan-qb-btn"
        className="flex items-center gap-2 rounded-md bg-[#16a34a] px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#15803d]">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanSearch className="h-4 w-4" />}
        {busy ? "Scanning…" : "Scan QB"}
      </button>

      {open && (
        <Overlay onClose={() => setOpen(false)}>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-bold text-white" data-testid="scan-qb-title">Scan QuickBooks invoices</h3>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close"
              data-testid="scan-qb-close" className="rounded p-1 text-white/40 hover:bg-white/10 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>

          {!busy && !done && (
            <div className="space-y-3" data-testid="scan-qb-form">
              <p className="text-xs text-white/50">
                Every QuickBooks invoice dated in this range gets pulled in. Ones already in the table (same invoice #) are skipped.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">From</span>
                  <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                    data-testid="scan-qb-from"
                    className="mt-1 w-full rounded-md border border-white/15 bg-white/[0.05] px-2.5 py-2 text-sm text-white [color-scheme:dark] focus:border-[#4f6fe8] focus:outline-none" />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">To</span>
                  <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                    data-testid="scan-qb-to"
                    className="mt-1 w-full rounded-md border border-white/15 bg-white/[0.05] px-2.5 py-2 text-sm text-white [color-scheme:dark] focus:border-[#4f6fe8] focus:outline-none" />
                </label>
              </div>
              <button type="button" onClick={start}
                data-testid="scan-qb-start"
                className="w-full rounded-md bg-[#16a34a] px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#15803d]">
                Start scan
              </button>
              <p className="text-center text-[10px] text-white/30">Runs on the server — you can close this window and come back.</p>
            </div>
          )}

          {busy && (
            <div className="space-y-3" data-testid="scan-qb-progress">
              <div className="flex items-center gap-2 text-sm text-white/80">
                <Loader2 className="h-4 w-4 animate-spin text-[#4ade80]" />
                Scanning QuickBooks…
              </div>
              <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 font-mono text-[11px] text-white/60">
                <p data-testid="scan-qb-fetched">
                  Pulled {running.fetched ?? 0}{running.total != null ? ` of ${running.total}` : ""} invoices
                </p>
                <p className="mt-1 text-[#4ade80]" data-testid="scan-qb-added">Added {running.added ?? 0}</p>
                <p className="mt-0.5 text-white/40" data-testid="scan-qb-skipped">Skipped {running.skipped ?? 0} already in the table</p>
              </div>
              <p className="text-[10px] text-white/30">
                Safe to close — the scan keeps running on the server and this window picks it back up when you return.
              </p>
            </div>
          )}

          {done && (
            <div className="space-y-3" data-testid="scan-qb-done">
              <p className="text-sm font-semibold text-[#4ade80]">Scan complete</p>
              <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 font-mono text-[11px] text-white/60">
                <p>Range: {done.date_from} → {done.date_to}</p>
                <p className="mt-1">Pulled {done.fetched ?? 0} invoices from QuickBooks</p>
                <p className="mt-1 text-[#4ade80]">Added {done.added ?? 0} new</p>
                <p className="mt-0.5 text-white/40">Skipped {done.skipped ?? 0} already in the table</p>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => { setDone(null); }}
                  data-testid="scan-qb-again"
                  className="rounded-md border border-white/15 px-4 py-2 text-xs font-semibold text-white/60 hover:text-white">
                  Scan another range
                </button>
                <button type="button" onClick={() => setOpen(false)}
                  data-testid="scan-qb-done-close"
                  className="rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1633a3]">
                  Done
                </button>
              </div>
            </div>
          )}
        </Overlay>
      )}
    </>
  );
}
